//MyAssignmentList.jsx
import React, { useCallback, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import useFetch from "../../hooks/useFetch";
import "react-datepicker/dist/react-datepicker.css";
import { useLocation } from "react-router-dom";
import Loader from "../../components/utils/Loader";
import Tooltip from "../../components/utils/Tooltip";

const MyAssignmentList = () => {
  const authState = useSelector((state) => state.authReducer);
  const [assignments, setAssignments] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);

  const fetchAssignments = useCallback(() => {
    const config = {
      url: "/assignment",
      method: "get",
      headers: { Authorization: authState.token },
    };

    fetchData(config, { showSuccessToast: false })
      .then((response) => {
        // console.log("Response:", response);
        if (response && Array.isArray(response.assignments)) {
          setAssignments(response.assignments);
        } else {
          console.error(
            "Invalid response format: Assignment not found in response"
          );
        }
      })
      .catch((error) => {
        console.error("Error fetching Assignments:", error);
      });
  }, [authState.token, fetchData]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchAssignments();
  }, [authState.isLoggedIn, fetchAssignments]);

  const handleView = (assignmentId) => {
    setSelectedAssignmentId((prevId) =>
      prevId === assignmentId ? null : assignmentId
    );
  };

  const handleCloseAssignmentDetails = () => {
    setSelectedAssignmentId(null);
  };

  const handleFromDateChange = (date) => {
    setFromDate(date);
  };

  const handleToDateChange = (date) => {
    setToDate(date);
  };

  const handleAssignmentClick = (assignmentId) => {
    setSelectedAssignmentId(assignmentId);
  };

  const handleAssignmentShare = (assignmentId) => {
    setSelectedAssignmentId(assignmentId);
  };

  const handleAssignmentSave = (assignmentId) => {
    setSelectedAssignmentId(assignmentId);
  };

  const handleAssignmentPrint = (assignmentId) => {
    setSelectedAssignmentId(assignmentId);
  };

  const formatDate = (date) => {
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, "0");
    const month = (jsDate.getMonth() + 1).toString().padStart(2, "0");
    const year = jsDate.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const sortedAssignments = [...assignments].sort(
    (a, b) => new Date(b.dateCreated) - new Date(a.dateCreated)
  );

  return (
    <>
      <div className="my-2 py-4">
        {/* <div>
          <h2 className="h-10 ml-4 mr-4 md:ml-0 text-white bg-orange-400">
            Your Assignments ({assignments.length})
          </h2>
        </div> */}
        <div className="border border-gray-300 p-4 mt-4">
          {loading ? (
            <Loader />
          ) : (
            <div className="text-white">
              {assignments && assignments.length === 0 ? (
                <div className="flex items-center justify-center text-yellow-300">
                  No assignment record found!
                </div>
              ) : (
                sortedAssignments.map((assignment, index) => (
                  <div
                    key={assignment._id}
                    className="bg-black my-4 p-4 text-white rounded-md shadow-md"
                  >
                    <div className="flex">
                      <span className="font-medium">
                        #{index + 1} ({assignment.concernArea})
                      </span>
                      <Tooltip text={"View details"}>
                        <span
                          className="ml-auto mr-4 text-blue-500 cursor-pointer"
                          onClick={() => handleView(assignment._id)}
                        >
                          <i className="fa-solid fa-eye"></i>
                        </span>
                      </Tooltip>
                      <Tooltip text={"Edit this record"} position={"top"}>
                        <Link
                          to={`/assignment/${assignment._id}`}
                          className="mr-4 text-green-600 cursor-pointer"
                        >
                          <i className="fa-solid fa-pen"></i>
                        </Link>
                      </Tooltip>
                    </div>
                    <div className="whitespace-pre  text-blue-700">
                      {assignment.assignmentType} : {assignment.concernArea}
                    </div>
                    {selectedAssignmentId === assignment._id && (
                      <div className="bg-black text-white">
                        <h2 className="text-blue-600">
                          <strong>{assignment.assignmentType} Details</strong>
                        </h2>
                        <p>
                          <strong>Assignment Type:</strong>{" "}
                          {assignment.assignmentType}
                        </p>
                        <p>
                          <strong>Date Created:</strong>{" "}
                          {formatDate(assignment.dateCreated)}
                        </p>
                        <p>
                          <strong>Concern Area:</strong>{" "}
                          {assignment.concernArea}
                        </p>
                        <p>
                          <strong>Statement:</strong>{" "}
                          {assignment.problemStatement}
                        </p>
                        <p>
                          <strong>Why1:</strong> {assignment.why1}
                        </p>
                        <p>
                          <strong>Why2:</strong> {assignment.why2}
                        </p>
                        <p>
                          <strong>Why3:</strong> {assignment.why3}
                        </p>
                        <p>
                          <strong>Why4:</strong> {assignment.why4}
                        </p>
                        <p>
                          <strong>Why5:</strong> {assignment.why5}
                        </p>
                        <p>
                          <strong>Root Cause:</strong> {assignment.rootCause}
                        </p>
                        <p>
                          <strong>Summary:</strong> {assignment.summary}
                        </p>
                        <p>
                          <strong>Remarks:</strong> {assignment.remarks}
                        </p>
                        <Tooltip text={"Share"} position={"top"}>
                          <span
                            className="text-green-500 cursor-pointer mr-4"
                            onClick={() =>
                              handleAssignmentShare(assignment._id)
                            }
                          >
                            <i className="fa-solid fa-share"></i>
                          </span>
                        </Tooltip>
                        <Tooltip text={"Save"} position={"top"}>
                          <span
                            className="text-blue-500 cursor-pointer mr-4"
                            onClick={() => handleAssignmentSave(assignment._id)}
                          >
                            <i className="fa-solid fa-save"></i>
                          </span>
                        </Tooltip>
                        <Tooltip text={"Print"} position={"top"}>
                          <span
                            className="text-orange-500 cursor-pointer mr-4"
                            onClick={() =>
                              handleAssignmentPrint(assignment._id)
                            }
                          >
                            <i className="fa-solid fa-print"></i>
                          </span>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MyAssignmentList;
